import { useState } from "react";

import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { IconButton, Link, Stack } from "@mui/material";
import { useClipboard } from "use-clipboard-copy";

import { copyMarkdownAsFormattedText } from "../utils/markdownToHtml";

type btnColor = undefined | "inherit" | "info" | "success" | "warning" | "error" | "default" | "primary" | "secondary";

type Props = {
  text: string;
  label?: string;
  color?: btnColor;
  onClick?: () => void;
  underline?: boolean;
  fontSize?: string;
  isMarkdown?: boolean;
};

export const CopyToClipboardButton = ({
  text,
  label,
  color,
  onClick,
  underline = true,
  fontSize,
  isMarkdown = false,
}: Props) => {
  const clipboard = useClipboard({
    copiedTimeout: 1400,
  });

  const [markdownCopied, setMarkdownCopied] = useState(false);

  const handleCopy = async (event) => {
    event.stopPropagation();

    if (isMarkdown) {
      const copySuccess = await copyMarkdownAsFormattedText(text);
      if (copySuccess) {
        setMarkdownCopied(true);
        setTimeout(() => {
          setMarkdownCopied(false);
        }, 1400);
      }
    } else {
      clipboard.copy(text);
    }

    onClick?.();
  };

  // Use markdownCopied state for markdown, clipboard.copied for regular text
  const isCopied = isMarkdown ? markdownCopied : clipboard.copied;

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        ml: -1,
      }}
    >
      <IconButton color={color} onClick={handleCopy} data-cy="copy-token-to-clipboard">
        {isCopied ? <CheckIcon color="primary" sx={{ fontSize }} /> : <ContentCopyIcon sx={{ fontSize }} />}
      </IconButton>
      <Link
        onClick={handleCopy}
        underline={underline ? "always" : "none"}
        color={color || "text.secondary"}
        variant="body2"
        sx={{ cursor: "pointer" }}
      >
        {label}
      </Link>
    </Stack>
  );
};
